import { onlineManager } from '@tanstack/react-query';
import { ApiResponse } from 'apisauce';
import { AxiosRequestConfig } from 'axios';
import FileSaver from 'file-saver';
import LangEnum from '../../../../lang/LangEnum';
import createRepository, { ErrorResponse, handlerApiError } from '../../../../lib/basicRepository';
import { BRAND_ID_HEADER_KEY, restApi } from '../../../../services/api';
import { GeneralSettingsParams } from '../../../../stores/GeneralSettings/generalSettings.types';
import snackbarStore from '../../../../stores/Snackbar.store';
import { createLinkToDownloadBlob } from '../../../../utils/createLinkToDownload';
import { offlineExportMultipleOrdersCsv } from '../../../offline/offlineRequestAdapters/onlineOrder.repository.offlineAdapters';
import { OrderProductDeliveryWindows } from '../../../products/stock/future-stock-v2/utils/dtos/order-product-delivery-windows.dto';
import { IShareOrderBody } from '../../../shared-entities/types/sharedEntities.types';
import {
  OrderConditionErrors,
  OrderError,
  OrderErrors,
  OrderSectionErrors,
} from '../../edit/sections/OrderEditSections.types';
import { ExportOrdersPdfParams } from '../../export/V2/services/exportOrder.types';
import { mapPdfScope } from '../../export/V2/services/exportOrderScope';
import {
  ValidateOrUpdateOrderConditions,
  ValidateOrUpdateOrderItemPrices,
} from '../../forceUpdate/types/forceUpdateOrder.types';
import {
  ExportMultipleCsvParams,
  ExportOrderCsvParams,
} from '../../list/mass-actions/types/orderListMassActions.types';
import { GetOrderPaymentHistoryDTO } from '../../order.types';
import { formatOrderFilters } from '../../orderFilter/orderFilter.utils';
import { ChangeOrderProductColor } from '../../products/interfaces/change-order-product-color.interface';
import {
  CreateOrUpdateOrderListWithKitParams,
  DeleteOrderProduct,
  OrderItemListGridItemDTO,
  OrderProduct,
  SortView,
  UpdateItemFormat,
} from '../../products/orderProducts.types';
import { ChangeOrderStatusBody } from './types/onlineOrdersRepository.types';
import { OrderPaymentDataDTO } from './types/orderPaymentRepository.types';

const { handleMessage } = snackbarStore;
const PATH = 'orders';
const DEFAULT_QUANTIDADE_SKU_COLUMN_INDEX = 27;

export const validateCsv = async (response: ApiResponse<Blob, Blob>) => {
  const ordersExpectedTotalPieces = Number(
    response.headers ? response.headers['x-orders-total-pieces'] : 0,
  );

  const data = await response.data?.text();
  if (!data) {
    return false;
  }

  const [header, ...rows] = data.split('\n');

  const quantidadeSkuColumnIndex = header.includes('quantidade_sku')
    ? header.split(';').indexOf('quantidade_sku')
    : DEFAULT_QUANTIDADE_SKU_COLUMN_INDEX; // caso o csv não tenha header

  const csvTotalPieces = rows
    .map((row) => row.split(';'))
    .reduce((total, row) => total + Number(row[quantidadeSkuColumnIndex] ?? 0), 0);

  return csvTotalPieces === ordersExpectedTotalPieces;
};

export const exportOrderCsv = async ({
  orderId,
  brandId,
  filename,
  translate,
}: ExportOrderCsvParams) => {
  let axiosConfig: AxiosRequestConfig = {
    responseType: 'blob',
  };

  if (brandId) {
    axiosConfig = {
      ...axiosConfig,
      headers: { [BRAND_ID_HEADER_KEY]: brandId },
    };
  }
  const response = await restApi.get<Blob>(`orders-export/${orderId}/csv`, {}, axiosConfig);

  const csvIsValid = await validateCsv(response);

  if (!csvIsValid) {
    handleMessage({
      type: 'error',
      message: translate(LangEnum.SNACKBAR_ERROR_PROCESSING_ORDER_DATA_CSV),
    });

    return response;
  }

  if (response.ok && response.data) {
    createLinkToDownloadBlob({
      data: response.data,
      filename,
      archiveType: 'csv',
    });
  } else {
    handleMessage({
      type: 'error',
      message: translate(LangEnum.SNACKBAR_ERROR_DOWNLOADING_ORDER_CSV),
    });
  }
};

export const onlineExportMultipleOrdersCsv = async ({
  searchParams,
  filename,
  brandId,
  translate,
}: ExportMultipleCsvParams) => {
  let axiosConfig: AxiosRequestConfig = {
    responseType: 'blob',
  };

  if (brandId) {
    axiosConfig = {
      ...axiosConfig,
      headers: { [BRAND_ID_HEADER_KEY]: brandId },
    };
  }
  const formattedParams = formatOrderFilters(searchParams);

  const response = await restApi.post<Blob>(
    `orders-export/csv`,
    { ...formattedParams },
    axiosConfig,
  );

  const csvIsValid = await validateCsv(response);

  if (!csvIsValid) {
    handleMessage({
      type: 'error',
      message: translate(LangEnum.SNACKBAR_ERROR_PROCESSING_ORDER_DATA_CSV),
    });

    return response;
  }

  if (response.ok && response.data) {
    const buffer = response.data;
    const blob = new Blob([buffer], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(blob, filename);
  } else {
    handleMessage({
      type: 'error',
      message: translate(LangEnum.SNACKBAR_ERROR_DOWNLOADING_ORDER_CSV),
    });
  }

  return response;
};

export const exportOrderPdf = async ({
  filename,
  scope,
  options,
  brandId,
  isSyncScope,
  selectedTab,
}: ExportOrdersPdfParams) => {
  const axiosConfig: AxiosRequestConfig = {
    ...(isSyncScope ? { responseType: 'blob' } : {}),
    ...(brandId ? { headers: { [BRAND_ID_HEADER_KEY]: brandId } } : {}),
  };

  const body = { brandId, filename, ...mapPdfScope(scope, selectedTab), ...options };

  const response = await restApi.post<any>('orders-export/pdf', body, axiosConfig);

  if (!response.ok) {
    throw new Error();
  }
  return response;
};
export const exportMultipleOrdersCsv = ({
  isOfflineMode = false,
  ...params
}: ExportMultipleCsvParams) =>
  isOfflineMode || !onlineManager.isOnline()
    ? offlineExportMultipleOrdersCsv(params)
    : onlineExportMultipleOrdersCsv(params);

export const onlineOrdersRepository = (id?: string) => {
  const repository = createRepository({
    path: PATH,
    id,
    restApi,
  });

  const updateOrderCustomer = async (customerId: string | null, allowUpdatePriceTable?: boolean) =>
    handlerApiError(
      await restApi.patch(`${PATH}/${id}/customer`, { customerId, allowUpdatePriceTable }),
    );

  const getOrderPaymentData = async () =>
    handlerApiError<OrderPaymentDataDTO>(await restApi.get(`/${PATH}/${id}/payment-data`));

  const authorizeOrderPayment = async () =>
    handlerApiError<void>(await restApi.post(`/${PATH}/${id}/authorize-payment`, {}));

  const createOrderPaymentLink = async (amountToPay: number) =>
    handlerApiError<{ paymentUrl: string }>(
      await restApi.post(`${PATH}/${id}/create-payment-link`, { amountToPay }),
    );

  const cancelOrderPaymentLink = async () =>
    handlerApiError<void>(await restApi.post(`${PATH}/${id}/cancel-payment-link`));

  const refundOrderPaymentLink = async () =>
    handlerApiError<void>(await restApi.post(`${PATH}/${id}/refund-payment-link`));

  const sendPaymentLinkEmails = async (emails: string[]) =>
    handlerApiError<void>(
      await restApi.post(`/${PATH}/${id}/send-payment-link-emails`, { emails }),
    );

  const getOrderProductsV2 = async (params?: { sortBy: SortView }) =>
    restApi.get<OrderProduct[]>(`${PATH}/${id}/order-products-v2`, params);

  const forceOrderSync = async () =>
    handlerApiError(await restApi.post(`/orders/${id}/force-sync`, {}));

  const forceReverseOrderSync = async () =>
    handlerApiError(await restApi.post(`integrations/orders/reverse/force/${id}`, {}));

  const validateOrUpdateOrderItemPrices = async (forceUpdate: boolean) =>
    handlerApiError<ValidateOrUpdateOrderItemPrices>(
      await restApi.patch(`${PATH}/${id}/order-items/update-or-validate-prices`, { forceUpdate }),
    );

  const validateOrUpdateOrderConditions = async (forceUpdate: boolean) =>
    handlerApiError<ValidateOrUpdateOrderConditions>(
      await restApi.patch(`${PATH}/${id}/update-or-validate-conditions`, { forceUpdate }),
    );

  const createOrUpdateOrderList = async (items: UpdateItemFormat[]) =>
    handlerApiError<OrderItemListGridItemDTO[]>(
      await restApi.post(`${PATH}/${id}/order-products/order-item-list`, items),
    );

  const createOrUpdateOrderListWithKit = async (params: CreateOrUpdateOrderListWithKitParams) =>
    handlerApiError<OrderItemListGridItemDTO[]>(
      await restApi.post<OrderItemListGridItemDTO[], ErrorResponse>(
        `${PATH}/${id}/order-products/kit-list`,
        params,
      ),
    );

  const getOrderErrors = async () =>
    restApi.get<ApiResponse<OrderErrors>>(`${PATH}/${id}/validate`);

  const getOrderGeneralDataErrors = async () =>
    handlerApiError<OrderSectionErrors>(await restApi.get(`${PATH}/${id}/validate-general-data`));

  const getOrderConditionsErrors = async () =>
    handlerApiError<OrderConditionErrors>(await restApi.get(`${PATH}/${id}/validate-conditions`));

  const createOrderProduct = async (items: Array<UpdateItemFormat>) =>
    handlerApiError<OrderProduct[]>(await restApi.post(`${PATH}/${id}/order-products`, { items }));

  const changeOrderProductDeliveryWindow = async (items: Array<UpdateItemFormat>) =>
    handlerApiError<OrderProduct>(
      await restApi.patch(`${PATH}/${id}/order-products/change-delivery-window`, { items }),
    );

  const deleteOrderProduct = async (data: DeleteOrderProduct) =>
    handlerApiError(await restApi.delete(`${PATH}/${id}/order-products`, {}, { data }));

  const getOrderProductDeliveryWindows = async (productId: string, productColorId: string) =>
    handlerApiError<OrderProductDeliveryWindows>(
      await restApi.get(
        `${PATH}/${id}/order-products/delivery-windows?productId=${productId}&productColorId=${productColorId}`,
      ),
    );

  const changeOrderProductColor = async ({
    productId,
    newProductColorId,
    availability,
    deliveryWindowId,
    orderItemIds,
  }: ChangeOrderProductColor) =>
    handlerApiError<OrderProduct>(
      await restApi.patch<OrderProduct, { data: OrderProduct; error: string }>(
        `/orders/${id}/order-products/change-color`,
        {
          newProductColorId,
          productId,
          availability,
          deliveryWindowId,
          orderItemIds,
        },
      ),
    );

  const getBrandSettings = async () =>
    handlerApiError<GeneralSettingsParams>(await restApi.get('/brands/general-settings/v1'));

  const getPaymentHistory = () =>
    restApi.get<GetOrderPaymentHistoryDTO[], { message: string }>(`/orders/${id}/payments`);

  const changeOrderStatus = async (body: ChangeOrderStatusBody, axiosConfig?: AxiosRequestConfig) =>
    handlerApiError(await restApi.patch(`/${PATH}/${id}/change-order-status`, body, axiosConfig));

  const shareOrder = (data: IShareOrderBody) => restApi.post(`/${PATH}/share`, data);

  const archiveOrder = async (axiosConfig: AxiosRequestConfig) =>
    handlerApiError(await restApi.patch(`/${PATH}/${id}/archive`, { archived: true }, axiosConfig));

  const unarchiveOrder = async (axiosConfig: AxiosRequestConfig) =>
    handlerApiError(
      await restApi.patch(`/${PATH}/${id}/archive`, { archived: false }, axiosConfig),
    );

  const getValidateWarnings = async () =>
    handlerApiError<{ value: OrderError }>(await restApi.get(`/${PATH}/${id}/validate-warnings`));

  return {
    ...repository,
    updateOrderCustomer,
    forceOrderSync,
    forceReverseOrderSync,
    createOrUpdateOrderList,
    createOrderProduct,
    deleteOrderProduct,
    getOrderProductDeliveryWindows,
    getBrandSettings,
    changeOrderProductColor,
    getOrderProductsV2,
    changeOrderProductDeliveryWindow,
    getOrderErrors,
    getOrderGeneralDataErrors,
    getOrderConditionsErrors,
    getPaymentHistory,
    validateOrUpdateOrderItemPrices,
    validateOrUpdateOrderConditions,
    changeOrderStatus,
    shareOrder,
    archiveOrder,
    unarchiveOrder,
    getValidateWarnings,
    createOrUpdateOrderListWithKit,
    authorizeOrderPayment,
    getOrderPaymentData,
    createOrderPaymentLink,
    sendPaymentLinkEmails,
    cancelOrderPaymentLink,
    refundOrderPaymentLink,
  };
};
